import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  EventService,
  LoadingScopesService,
  ProductActions,
  ProductLoadingService,
  StateWithProduct,
  withdrawOn,
} from 'haba-core';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { isPlatformBrowser } from '@angular/common';
import { merge, Observable, of, SchedulerLike, defer } from 'rxjs';
import { delay, filter, mapTo, tap } from 'rxjs/operators';

/** fix for issue https://github.com/SAP/spartacus/issues/14415  */

@Injectable({
  providedIn: 'root',
})
export class HabaProductLoadingService extends ProductLoadingService {
  constructor(
    store: Store<StateWithProduct>,
    loadingScopes: LoadingScopesService,
    actions$: Actions,
    @Inject(PLATFORM_ID) platformId: any,
    eventService: EventService
  ) {
    super(store, loadingScopes, actions$, platformId, eventService);
  }

  protected getProductReloadTriggers(productCode: string, scope: string): Observable<unknown>[] {
    const triggers: Observable<unknown>[] = [];

    // max age trigger add
    const maxAge = this.loadingScopes.getMaxAge('product', scope);
    if (maxAge && isPlatformBrowser(this.platformId)) {
      // we want to grab load product success and load product fail for this product and scope
      const loadFinish$ = this.actions$.pipe(
        ofType(ProductActions.LOAD_PRODUCT_SUCCESS, ProductActions.LOAD_PRODUCT_FAIL),
        filter(
          (action: ProductActions.LoadProductSuccess | ProductActions.LoadProductFail) =>
            action.meta.entityId === productCode && action.meta.scope === scope
        )
      );

      const loadStart$ = this.actions$.pipe(
        ofType(ProductActions.LOAD_PRODUCT),
        filter(
          (action: ProductActions.LoadProduct) =>
            action.payload === productCode && action.meta.scope === scope
        )
      );

      triggers.push(this.getMaxAgeTriggerHaba(loadStart$, loadFinish$, maxAge));
    }

    const reloadTriggers$ = this.loadingScopes
      .getReloadTriggers('product', scope)
      .map((e) => this.eventService.get(e));

    return triggers.concat(reloadTriggers$);
  }

  private getMaxAgeTriggerHaba(
    loadStart$: Observable<ProductActions.ProductAction>,
    loadFinish$: Observable<ProductActions.ProductAction>,
    maxAge: number,
    scheduler?: SchedulerLike
  ): Observable<boolean> {
    let timestamp = 0;

    const now = () => (scheduler ? scheduler.now() : Date.now());

    const timestamp$ = loadFinish$.pipe(tap(() => (timestamp = now())));

    const shouldReload$: Observable<boolean> = defer(() => {
      const age = now() - timestamp;

      const timestampRefresh$ = timestamp$.pipe(
        delay(maxAge, scheduler),
        mapTo(true),
        withdrawOn(loadStart$)
      );

      if (age > maxAge) {
        // we should emit first value immediately
        return merge(of(true), timestampRefresh$);
      } else if (age === 0) {
        // edge case, we should emit max age timeout after next load success
        // could happen with artificial schedulers
        return timestampRefresh$;
      } else {
        // we should emit first value when age will expire
        return merge(of(true).pipe(delay(maxAge - age, scheduler)), timestampRefresh$);
      }
    });

    return shouldReload$;
  }
}
